import axios from 'axios';
import {
  ManagedUser,
  Role,
  CreateUserPayload,
  UpdateUserPayload,
} from '../types/user';
import { ApiErrorResponse } from '../types/auth';
import { PaginationParams, PaginatedResponse, buildQueryString } from '../types/pagination';
import { ROLE_ENDPOINTS, USER_MANAGEMENT_ENDPOINTS } from '../utils/endPoints';
import { createApiClient } from '../utils/axiosConfig';

const apiClient = createApiClient();

export const getAllRoles = async (params?: PaginationParams): Promise<PaginatedResponse<Role>> => {
  try {
    const qs = buildQueryString(params ?? {});
    const response = await apiClient.get<{ data: Role[]; total: number; page: number; limit: number; totalPages: number }>(
      `${ROLE_ENDPOINTS.ALL}${qs}`
    );
    const d = response.data;
    return {
      items: Array.isArray(d.data) ? d.data : (d as unknown as { data?: Role[] }).data ?? [],
      total: d.total ?? 0,
      page: d.page ?? 1,
      limit: d.limit ?? 20,
      totalPages: d.totalPages ?? 0,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch roles');
    }
    throw new Error('An unexpected error occurred while fetching roles.');
  }
};

export const getAllUsers = async (params?: PaginationParams): Promise<PaginatedResponse<ManagedUser>> => {
  try {
    const qs = buildQueryString(params ?? {});
    const response = await apiClient.get<{ users: ManagedUser[]; total: number; page: number; limit: number; totalPages: number }>(
      `${USER_MANAGEMENT_ENDPOINTS.ALL}${qs}`
    );
    const d = response.data;
    return {
      items: d.users ?? [],
      total: d.total ?? 0,
      page: d.page ?? 1,
      limit: d.limit ?? 20,
      totalPages: d.totalPages ?? 0,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch users');
    }
    throw new Error('An unexpected error occurred while fetching users.');
  }
};

export const createUser = async (
  payload: CreateUserPayload
): Promise<{ user: ManagedUser; message: string }> => {
  try {
    const response = await apiClient.post<{ user: ManagedUser; message: string }>(
      USER_MANAGEMENT_ENDPOINTS.CREATE,
      payload
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to create user');
    }
    throw new Error('An unexpected error occurred while creating user.');
  }
};

export const updateUser = async (
  userId: number,
  payload: UpdateUserPayload,
  profileImage?: File
): Promise<{ user: ManagedUser; message: string }> => {
  try {
    let dataToSend: UpdateUserPayload | FormData = payload;
    if (profileImage) {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });
      formData.append('profileImage', profileImage);
      dataToSend = formData;
    }
    const response = await apiClient.put<{ user: ManagedUser; message: string }>(
      USER_MANAGEMENT_ENDPOINTS.UPDATE(userId),
      dataToSend
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to update user');
    }
    throw new Error('An unexpected error occurred while updating user.');
  }
};

export const deleteUser = async (userId: number): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete<{ message: string }>(
      USER_MANAGEMENT_ENDPOINTS.DELETE(userId)
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to delete user');
    }
    throw new Error('An unexpected error occurred while deleting user.');
  }
};
